import React, { useState, useEffect, useMemo } from "react";
import {
  BiPlus,
  BiGift,
  BiListUl,
  BiCalendar,
  BiSearch,
  BiCopy,
  BiCheckCircle,
  BiChevronLeft,
  BiChevronRight,
  BiDetail,
  BiPackage,
  BiXCircle,
  BiSearchAlt,
  BiCheckSquare,
} from "react-icons/bi";
import { toast } from "react-toastify";
import GiftCardCreateForm from "./../../components/admin/GiftCardCreateForm";
import useGiftCardStore from "./../../store/useGiftCardStore";
import axiosToken from "../../api/axiosClient";

// Composant Modal de Vérification
function GiftCardVerifyModal({ isOpen, onClose, allCards, onCardUsed }) {
  const [searchTerm, setSearchTerm] = useState("CAD_");
  const [selectedSubcard, setSelectedSubcard] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isUsing, setIsUsing] = useState(false);

  // Extraire toutes les sous-cartes de toutes les cartes principales
  const allSubcards = useMemo(() => {
    if (!allCards) return [];

    const subcards = [];
    allCards.forEach((card) => {
      if (card.giftCards && Array.isArray(card.giftCards)) {
        card.giftCards.forEach((subcard) => {
          subcards.push({
            ...subcard,
            parentCard: card, // Référence à la carte principale
          });
        });
      }
    });
    return subcards;
  }, [allCards]);

  // Filtrer les sous-cartes en fonction de la recherche
  const filteredSubcards = useMemo(() => {
    if (!searchTerm || searchTerm === "CAD_") return [];

    const term = searchTerm.toLowerCase();
    return allSubcards
      .filter((subcard) => subcard.code.toLowerCase().includes(term))
      .slice(0, 10); // Limiter à 10 résultats pour l'UX
  }, [searchTerm, allSubcards]);

  // Rechercher une carte principale d'abord, puis ses sous-cartes
  const filteredMainCards = useMemo(() => {
    if (!searchTerm || searchTerm === "CAD_") return [];

    const term = searchTerm.toLowerCase();
    return allCards
      .filter((card) => card.code.toLowerCase().includes(term))
      .slice(0, 5); // Limiter à 5 cartes principales
  }, [searchTerm, allCards]);

  const handleSearch = async () => {
    if (!searchTerm || searchTerm === "CAD_") {
      toast.warning("Veuillez entrer un code de carte");
      return;
    }

    setIsSearching(true);

    // Chercher d'abord dans les sous-cartes
    const foundSubcard = allSubcards.find(
      (subcard) => subcard.code === searchTerm,
    );

    if (foundSubcard) {
      setSelectedSubcard(foundSubcard);
      setIsSearching(false);
      return;
    }

    // Si non trouvé, chercher dans les cartes principales
    const foundMainCard = allCards.find((card) => card.code === searchTerm);

    if (foundMainCard) {
      toast.info(
        `Carte principale trouvée. Veuillez sélectionner une sous-carte spécifique.`,
      );
    } else {
      toast.error("Aucune carte trouvée avec ce code");
    }

    setIsSearching(false);
  };

  const handleSelectSubcard = (subcard) => {
    setSelectedSubcard(subcard);
    setSearchTerm(subcard.code);
  };

  const handleUseSubcard = async () => {
    if (!selectedSubcard) return;

    if (selectedSubcard.used) {
      toast.warning("Cette sous-carte est déjà utilisée");
      return;
    }

    if (
      window.confirm(
        `Êtes-vous sûr de vouloir marquer la sous-carte ${selectedSubcard.code} comme utilisée ?`,
      )
    ) {
      setIsUsing(true);
      try {
        // Requête PATCH pour marquer la sous-carte comme utilisée
        const response = await axiosToken.patch(
          `/gift-card/use/code/${selectedSubcard.code}`,
        );

        if (!response) {
          throw new Error("Erreur lors de la mise à jour");
        }

        toast.success("Carte marquée comme utilisée avec succès");

        // Recharger les données
        if (onCardUsed) onCardUsed();

        // Fermer le modal après un délai
        setTimeout(() => {
          handleClose();
        }, 1500);
      } catch (error) {
        console.error("Erreur lors de l'utilisation de la sous-carte:", error);
        toast.error("Erreur lors de la mise à jour de la carte");
      } finally {
        setIsUsing(false);
      }
    }
  };

  const handleClose = () => {
    setSearchTerm("CAD_");
    setSelectedSubcard(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl flex flex-col h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Vérifier une sous-carte cadeau
            </h2>
            <p className="font-nunito text-sm text-text mt-1">
              Recherchez et vérifiez l'état d'une sous-carte cadeau
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Barre de recherche */}
          <div className="mb-6">
            <label className="font-poppins font-medium text-sm text-title mb-2 block">
              Code de la sous-carte
            </label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSelectedSubcard(null);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    placeholder="CAD_"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 font-nunito text-sm text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                  <BiSearchAlt
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !searchTerm || searchTerm === "CAD_"}
                  className="flex items-center gap-2 px-4 py-3 bg-secondary-600 text-white font-nunito font-semibold text-sm hover:bg-secondary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Recherche...
                    </>
                  ) : (
                    <>
                      <BiSearch size={16} />
                      Rechercher
                    </>
                  )}
                </button>
              </div>

              {/* Suggestions de sous-cartes */}
              {searchTerm.length > 4 &&
                filteredSubcards.length > 0 &&
                !selectedSubcard && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto">
                    {filteredSubcards.map((subcard) => (
                      <button
                        key={subcard.id}
                        onClick={() => handleSelectSubcard(subcard)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-mono text-sm">{subcard.code}</p>
                            <p className="font-nunito text-xs text-text">
                              Carte principale: {subcard.parentCard.code}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs ${subcard.used ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}
                          >
                            {subcard.used ? "Utilisée" : "Disponible"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

              {/* Suggestions de cartes principales */}
              {searchTerm.length > 4 &&
                filteredMainCards.length > 0 &&
                !selectedSubcard &&
                filteredSubcards.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto">
                    <div className="px-4 py-2 bg-gray-50 border-b">
                      <p className="font-nunito text-xs text-text">
                        Cartes principales trouvées :
                      </p>
                    </div>
                    {filteredMainCards.map((card) => (
                      <div
                        key={card.id}
                        className="w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="font-mono text-sm mb-1">{card.code}</p>
                        <p className="font-nunito text-xs text-text">
                          {card.giftCards?.length || 0} sous-carte(s)
                          disponible(s)
                        </p>
                        {card.giftCards?.slice(0, 3).map((subcard, idx) => (
                          <button
                            key={subcard.id}
                            onClick={() =>
                              handleSelectSubcard({
                                ...subcard,
                                parentCard: card,
                              })
                            }
                            className="mt-1 ml-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            {subcard.code}
                          </button>
                        ))}
                        {card.giftCards?.length > 3 && (
                          <p className="font-nunito text-xs text-text mt-1 ml-2">
                            ... et {card.giftCards.length - 3} autre(s)
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <p className="font-nunito text-xs text-gray-500 mt-2">
              Toutes les sous-cartes commencent par "CAD_". Entrez le code
              complet pour une recherche précise.
            </p>
          </div>

          {/* Résultats de la recherche */}
          {selectedSubcard && (
            <div className="bg-gray-50 p-6 border border-gray-200">
              <h3 className="font-poppins font-semibold text-lg text-title mb-4">
                Résultat de la recherche
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations de la sous-carte */}
                <div className="space-y-4">
                  <div>
                    <p className="font-nunito text-sm text-text mb-1">
                      Code sous-carte
                    </p>
                    <p className="font-mono text-lg font-semibold">
                      {selectedSubcard.code}
                    </p>
                  </div>

                  <div>
                    <p className="font-nunito text-sm text-text mb-1">Statut</p>
                    <span
                      className={`px-3 py-2 inline-flex items-center gap-2 ${selectedSubcard.used ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}
                    >
                      {selectedSubcard.used ? (
                        <>
                          <BiXCircle size={16} />
                          Déjà utilisée
                        </>
                      ) : (
                        <>
                          <BiCheckCircle size={16} />
                          Disponible
                        </>
                      )}
                    </span>
                  </div>

                  <div>
                    <p className="font-nunito text-sm text-text mb-1">
                      Date de création
                    </p>
                    <div className="flex items-center gap-2">
                      <BiCalendar size={14} className="text-gray-500" />
                      <span className="font-nunito text-sm">
                        {new Date(
                          selectedSubcard.created_at,
                        ).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Informations de la carte principale */}
                <div className="space-y-4">
                  <div>
                    <p className="font-nunito text-sm text-text mb-1">
                      Carte principale associée
                    </p>
                    <p className="font-mono text-sm">
                      {selectedSubcard.parentCard.code}
                    </p>
                  </div>

                  <div>
                    <p className="font-nunito text-sm text-text mb-1">
                      Service
                    </p>
                    <p className="font-poppins font-medium">
                      {selectedSubcard.parentCard.service?.nom || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="font-nunito text-sm text-text mb-1">
                      Valeur unitaire
                    </p>
                    <p className="font-poppins font-bold text-secondary-600">
                      {parseFloat(
                        selectedSubcard.parentCard.value || 0,
                      ).toLocaleString()}{" "}
                      FCFA
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                {selectedSubcard.used ? (
                  <div className="text-center">
                    <p className="font-nunito text-sm text-red-600">
                      Cette sous-carte a déjà été utilisée.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleUseSubcard}
                      disabled={isUsing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-nunito font-semibold text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUsing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Traitement...
                        </>
                      ) : (
                        <>
                          <BiCheckSquare size={16} />
                          Marquer comme utilisée
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSubcard(null);
                        setSearchTerm("CAD_");
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 font-nunito text-sm hover:bg-gray-50 transition-colors"
                    >
                      Nouvelle recherche
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Modal de Détails
function GiftCardDetailModal({ isOpen, onClose, card, onCardUsed }) {
  const { setAsUsedGiftCard, isUsing } = useGiftCardStore();
  const [activeTab, setActiveTab] = useState("details");

  if (!isOpen || !card) return null;

  const handleUseCard = async () => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir marquer cette carte comme utilisée ?`,
      )
    ) {
      try {
        await setAsUsedGiftCard(card.id);
        toast.success("Carte marquée comme utilisée");
        if (onCardUsed) onCardUsed();
        onClose();
      } catch (error) {
        console.error("Erreur lors de l'utilisation de la carte:", error);
        toast.error("Erreur lors de l'utilisation de la carte");
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "UNUSED":
        return "Active";
      case "USED":
        return "Utilisée";
      case "ALL_USED":
        return "Tous utilisés";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UNUSED":
        return "bg-green-100 text-green-800";
      case "USED":
        return "bg-red-100 text-red-800";
      case "ALL_USED":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Calcul des sous-cartes utilisées/non utilisées
  const usedCards = card.giftCards?.filter((g) => g.used) || [];
  const unusedCards = card.giftCards?.filter((g) => !g.used) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Détails de la carte cadeau
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 flex items-center gap-1">
                <BiGift size={14} />
                Carte Cadeau
              </span>
              <span
                className={`px-3 py-1 text-sm font-medium ${getStatusColor(card.status)}`}
              >
                {getStatusLabel(card.status)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Onglets */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab("details")}
                className={`py-3 px-1 border-b-2 font-nunito font-semibold text-sm ${
                  activeTab === "details"
                    ? "border-secondary-600 text-secondary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Informations générales
              </button>
              <button
                onClick={() => setActiveTab("subcards")}
                className={`py-3 px-1 border-b-2 font-nunito font-semibold text-sm ${
                  activeTab === "subcards"
                    ? "border-secondary-600 text-secondary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Sous-cartes ({card.giftCards?.length || 0})
              </button>
            </div>
          </div>

          {activeTab === "details" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Informations de base */}
              <div className="space-y-4">
                <h3 className="font-poppins font-semibold text-lg text-title flex items-center gap-2">
                  <BiGift className="text-purple-600" />
                  Informations de la carte
                </h3>

                <div>
                  <p className="font-nunito text-sm text-text mb-1">
                    Code principal
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="bg-gray-100 px-3 py-2 font-nunito text-sm text-text flex-1">
                      {card.code}
                    </span>
                    <button
                      onClick={() => copyToClipboard(card.code)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copier le code"
                    >
                      <BiCopy size={16} />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="font-nunito text-sm text-text mb-1">Statut</p>
                  <span
                    className={`px-3 py-1 font-nunito text-sm ${getStatusColor(card.status)}`}
                  >
                    {getStatusLabel(card.status)}
                  </span>
                </div>

                <div>
                  <p className="font-nunito text-sm text-text mb-1">
                    Valeur unitaire
                  </p>
                  <p className="font-poppins font-bold text-lg text-montant">
                    {parseFloat(card.value || 0).toLocaleString()} FCFA
                  </p>
                </div>

                <div>
                  <p className="font-nunito text-sm text-text mb-1">
                    Quantité totale
                  </p>
                  <p className="font-poppins font-medium text-lg">
                    {card.quantity}
                  </p>
                </div>
              </div>

              {/* Service et montants */}
              <div className="space-y-4">
                <h3 className="font-poppins font-semibold text-lg text-title flex items-center gap-2">
                  <BiPackage className="text-blue-600" />
                  Service associé
                </h3>

                {card.service && (
                  <div className="bg-gray-50 p-4">
                    <div className="space-y-3">
                      <div>
                        <p className="font-nunito text-sm text-text">Service</p>
                        <p className="font-poppins font-medium">
                          {card.service.nom}
                        </p>
                      </div>
                      <div>
                        <p className="font-nunito text-sm text-text">
                          Code service
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm">
                            {card.service.code}
                          </p>
                          <button
                            onClick={() => copyToClipboard(card.service.code)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copier le code"
                          >
                            <BiCopy size={14} />
                          </button>
                        </div>
                      </div>
                      {card.service.description && (
                        <div>
                          <p className="font-nunito text-sm text-text">
                            Description
                          </p>
                          <p className="font-nunito text-sm">
                            {card.service.description}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="font-nunito text-sm text-text">Durée</p>
                        <p className="font-nunito text-sm">
                          {card.service.durationMinutes} minutes
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <p className="font-nunito text-sm text-text mb-1">
                    Valeur totale
                  </p>
                  <p className="font-poppins font-bold text-xl text-secondary-600">
                    {parseFloat(card.totalValue || 0).toLocaleString()} FCFA
                  </p>
                </div>

                <div>
                  <p className="font-nunito text-sm text-text mb-1">
                    Date de création
                  </p>
                  <div className="flex items-center gap-2 font-nunito text-sm text-text">
                    <BiCalendar size={16} />
                    <span>
                      {new Date(card.created_at).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "subcards" && (
            <div>
              <h3 className="font-poppins font-semibold text-lg text-title mb-4 flex items-center gap-2">
                <BiListUl className="text-secondary-600" />
                Sous-cartes générées ({card.giftCards?.length || 0})
              </h3>

              {/* Résumé des sous-cartes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <BiCheckCircle className="text-green-600" size={20} />
                    <p className="font-poppins font-medium text-sm">
                      Non utilisées
                    </p>
                  </div>
                  <p className="font-poppins font-bold text-xl text-green-600">
                    {unusedCards.length}
                  </p>
                </div>
                <div className="bg-red-50 p-4 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <BiXCircle className="text-red-600" size={20} />
                    <p className="font-poppins font-medium text-sm">
                      Utilisées
                    </p>
                  </div>
                  <p className="font-poppins font-bold text-xl text-red-600">
                    {usedCards.length}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <BiGift className="text-blue-600" size={20} />
                    <p className="font-poppins font-medium text-sm">Total</p>
                  </div>
                  <p className="font-poppins font-bold text-xl text-blue-600">
                    {card.giftCards?.length || 0}
                  </p>
                </div>
              </div>

              {/* Liste des sous-cartes */}
              {card.giftCards?.length === 0 ? (
                <p className="font-nunito text-sm text-text text-center py-8">
                  Aucune sous-carte générée
                </p>
              ) : (
                <div className="space-y-3">
                  {card.giftCards?.map((subcard, index) => (
                    <div
                      key={subcard.id}
                      className={`flex items-center justify-between p-4 border ${
                        subcard.used
                          ? "border-red-200 bg-red-50"
                          : "border-green-200 bg-green-50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span
                            className={`p-2 rounded-full ${subcard.used ? "bg-red-100" : "bg-green-100"}`}
                          >
                            {subcard.used ? (
                              <BiXCircle className="text-red-600" size={16} />
                            ) : (
                              <BiCheckCircle
                                className="text-green-600"
                                size={16}
                              />
                            )}
                          </span>
                          <div>
                            <p className="font-poppins font-medium">
                              Sous-carte #{index + 1}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="font-mono text-sm bg-white px-2 py-1">
                                {subcard.code}
                              </span>
                              <button
                                onClick={() => copyToClipboard(subcard.code)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Copier le code"
                              >
                                <BiCopy size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span
                          className={`px-3 py-1 text-sm ${subcard.used ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}
                        >
                          {subcard.used ? "Utilisée" : "Disponible"}
                        </span>
                        <p className="font-nunito text-xs text-text mt-1">
                          {new Date(subcard.created_at).toLocaleDateString(
                            "fr-FR",
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
          {card.status === "UNUSED" && (
            <button
              onClick={handleUseCard}
              disabled={isUsing}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-nunito font-semibold text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUsing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Traitement...
                </>
              ) : (
                <>
                  <BiCheckCircle size={16} />
                  Marquer comme utilisée
                </>
              )}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Card pour mobile
function GiftCardCard({ card, onViewDetails }) {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copié dans le presse-papier");
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "UNUSED":
        return "Active";
      case "USED":
        return "Utilisée";
      case "ALL_USED":
        return "Tous utilisés";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UNUSED":
        return "bg-green-100 text-green-800";
      case "USED":
        return "bg-red-100 text-red-800";
      case "ALL_USED":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Calcul des sous-cartes utilisées
  const usedCards = card.giftCards?.filter((g) => g.used)?.length || 0;
  const totalCards = card.giftCards?.length || 0;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Ligne 1: Code + Statut */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1 shrink-0">
              <BiGift size={12} />
              Carte
            </span>
            <div className="min-w-0">
              <p className="font-mono text-sm truncate" title={card.code}>
                {card.code}
              </p>
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs ${getStatusColor(card.status)} shrink-0`}
          >
            {getStatusLabel(card.status)}
          </span>
        </div>

        {/* Ligne 2: Service + Montant */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BiPackage className="text-gray-400" size={16} />
            <span className="font-nunito font-medium text-sm truncate max-w-[150px]">
              {card.service?.nom || "N/A"}
            </span>
          </div>
          <span className="font-poppins font-bold text-base text-montant">
            {parseFloat(card.value || 0).toLocaleString()} FCFA
          </span>
        </div>
      </div>

      {/* Ligne 3: Détails quantité + Date */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <BiListUl size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {card.quantity} carte{card.quantity !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <BiCalendar size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {new Date(card.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="font-nunito text-xs text-text">Utilisation</p>
              <p className="font-nunito text-xs font-medium">
                {usedCards}/{totalCards}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ligne 4: Actions */}
      <div className="p-3 bg-gray-50 flex items-center justify-between">
        <button
          onClick={() => copyToClipboard(card.code)}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
          title="Copier le code"
        >
          <BiCopy size={12} />
          Copier
        </button>

        <button
          onClick={() => onViewDetails(card)}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <BiDetail size={14} />
          Détails
        </button>
      </div>
    </div>
  );
}

const GiftCardsPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [selectedCard, setSelectedCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // États pour le tri
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { getAllGiftCards, giftCards, isLoading, setAsUsedGiftCard } =
    useGiftCardStore();

  useEffect(() => {
    loadGiftCards();
  }, []);

  const loadGiftCards = async () => {
    try {
      await getAllGiftCards();
    } catch (error) {
      console.error("Erreur lors du chargement des cartes cadeau:", error);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadGiftCards();
  };

  const handleUseCard = async (card) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir marquer la carte ${card.code} comme utilisée ?`,
      )
    ) {
      try {
        await setAsUsedGiftCard(card.id);
        toast.success("Carte marquée comme utilisée");
        loadGiftCards();
      } catch (error) {
        console.error("Erreur lors de l'utilisation de la carte:", error);
      }
    }
  };

  const getPeriodDates = (period) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;

      case "week":
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() + (6 - today.getDay()));
        end.setHours(23, 59, 59, 999);
        break;

      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;

      default:
        return { startDate: "", endDate: "" };
    }

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  };

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);
    setCurrentPage(1);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
    } else if (period !== "custom") {
      const dates = getPeriodDates(period);
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
    }
  };

  useEffect(() => {
    if (periodFilter === "custom" && startDate && endDate) {
      const timer = setTimeout(() => {
        setCurrentPage(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, periodFilter]);

  const resetFilters = () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setCurrentPage(1);
    setSortField("created_at");
    setSortDirection("desc");
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return startDate && endDate
          ? `Période du ${new Date(startDate).toLocaleDateString("fr-FR")} au ${new Date(endDate).toLocaleDateString("fr-FR")}`
          : "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "UNUSED":
        return "Active";
      case "USED":
        return "Utilisée";
      case "ALL_USED":
        return "Tous utilisés";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UNUSED":
        return "bg-green-100 text-green-800";
      case "USED":
        return "bg-red-100 text-red-800";
      case "ALL_USED":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  // Fonction pour ouvrir le modal des détails
  const handleShowDetails = (card) => {
    setSelectedCard(card);
    setIsModalOpen(true);
  };

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCard(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copié dans le presse-papier");
  };

  // Filtrage et tri des cartes
  const filteredAndSortedCards = useMemo(() => {
    if (!giftCards) return [];

    let filtered = giftCards.filter((card) => {
      // Filtre par recherche
      const matchesSearch =
        !searchTerm ||
        card.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.service?.nom?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtre par période
      let matchesDate = true;
      if (periodFilter !== "all" && startDate && endDate) {
        const cardDate = new Date(card.created_at);
        const filterStartDate = new Date(startDate);
        const filterEndDate = new Date(endDate);

        // Définir les heures pour couvrir toute la journée
        filterStartDate.setHours(0, 0, 0, 0);
        filterEndDate.setHours(23, 59, 59, 999);

        matchesDate = cardDate >= filterStartDate && cardDate <= filterEndDate;
      }

      return matchesSearch && matchesDate;
    });

    // Tri des résultats
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "code":
          aValue = a.code || "";
          bValue = b.code || "";
          break;
        case "service":
          aValue = a.service?.nom || "";
          bValue = b.service?.nom || "";
          break;
        case "value":
          aValue = parseFloat(a.value || 0);
          bValue = parseFloat(b.value || 0);
          break;
        case "quantity":
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    giftCards,
    searchTerm,
    periodFilter,
    startDate,
    endDate,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedCards.length / itemsPerPage);
  const paginatedCards = filteredAndSortedCards.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Code",
      field: "code",
      width: "15%",
      sortable: true,
      render: (card) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-mono text-xs font-medium truncate"
              title={card.code}
            >
              {card.code}
            </span>
            <button
              onClick={() => copyToClipboard(card.code)}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              title="Copier le code"
            >
              <BiCopy size={12} />
            </button>
          </div>
        </div>
      ),
    },
    {
      headerName: "Service",
      field: "service",
      width: "20%",
      sortable: true,
      render: (card) => (
        <div className="font-nunito text-sm">{card.service?.nom || "N/A"}</div>
      ),
    },
    {
      headerName: "Valeur",
      field: "value",
      width: "15%",
      sortable: true,
      render: (card) => (
        <div className="font-poppins font-bold text-sm text-montant">
          {parseFloat(card.value || 0).toLocaleString()} FCFA
        </div>
      ),
    },
    {
      headerName: "Quantité",
      field: "quantity",
      width: "10%",
      sortable: true,
      render: (card) => (
        <div className="flex items-center gap-1">
          <BiListUl size={14} className="text-gray-500" />
          <span className="font-nunito text-xs">{card.quantity || 0}</span>
        </div>
      ),
    },
    {
      headerName: "Statut",
      field: "status",
      width: "12%",
      sortable: true,
      render: (card) => (
        <span className={`px-2 py-1 text-xs ${getStatusColor(card.status)}`}>
          {getStatusLabel(card.status)}
        </span>
      ),
    },
    {
      headerName: "Date création",
      field: "created_at",
      width: "15%",
      sortable: true,
      render: (card) => (
        <div className="font-nunito text-xs text-text">
          {new Date(card.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "",
      width: "16%",
      render: (card) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleShowDetails(card)}
            className="px-3 py-1.5 text-xs bg-secondary-100 hover:bg-secondary-200 transition-colors flex items-center justify-center gap-1"
          >
            <BiDetail size={12} />
            Détails
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="font-poppins font-semibold text-[18px] text-title">
            Cartes Cadeau
          </h1>
          <p className="font-nunito text-[14px] text-text mt-1">
            Gérez vos cartes cadeau - Création, consultation et utilisation
          </p>
        </div>

        {/* <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-secondary-600 text-white px-4 py-2 font-nunito font-semibold text-[14px] hover:bg-secondary-700 transition-colors"
        >
          <BiPlus size={20} />
          Créer une carte cadeau
        </button> */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowVerifyModal(true)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 font-nunito font-semibold text-[14px] hover:bg-green-700 transition-colors"
          >
            <BiCheckSquare size={18} />
            Vérifier une sous-carte
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-secondary-600 text-white px-4 py-2 font-nunito font-semibold text-[14px] hover:bg-secondary-700 transition-colors"
          >
            <BiPlus size={20} />
            Créer une carte cadeau
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col xl:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <BiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Rechercher par code ou service..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
          </div>
        </div>

        {/* Indicateur de période active */}
        {(periodFilter !== "all" || searchTerm) && (
          <div className="mt-3 flex items-center gap-2 font-nunito text-[14px]">
            <span className="text-gray-600">Filtres actifs:</span>
            {periodFilter !== "all" && (
              <span className="bg-secondary-100 text-secondary-800 px-2 py-1 text-xs">
                {getPeriodLabel()}
              </span>
            )}
            {searchTerm && (
              <span className="bg-green-100 text-green-800 px-2 py-1 text-xs">
                Recherche: "{searchTerm}"
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-2 py-1 text-xs">
              {filteredAndSortedCards.length} résultat
              {filteredAndSortedCards.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Pagination en haut */}
          {totalPages > 1 && filteredAndSortedCards.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedCards.length} élément
                {filteredAndSortedCards.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-poppins font-bold text-xs text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.sortable ? (
                          <button
                            onClick={() => handleSort(column.field)}
                            className="flex items-center gap-1 hover:text-secondary-600"
                          >
                            {column.headerName}
                            {sortField === column.field && (
                              <span className="text-xs">
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </button>
                        ) : (
                          column.headerName
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedCards.map((card, index) => (
                    <tr
                      key={card.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="flex items-center h-full">
                            {column.render(card)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile */}
          <div className="lg:hidden space-y-3 mb-6">
            {paginatedCards.map((card) => (
              <GiftCardCard
                key={card.id}
                card={card}
                onViewDetails={handleShowDetails}
              />
            ))}
          </div>

          {/* Pagination en bas */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedCards.length} élément
                {filteredAndSortedCards.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredAndSortedCards.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <BiGift className="mx-auto text-gray-300" size={40} />
              <p className="font-nunito text-sm text-text mt-3">
                {searchTerm || periodFilter !== "all"
                  ? "Aucun résultat trouvé"
                  : "Aucune carte cadeau pour cette période"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de vérification */}
      <GiftCardVerifyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        allCards={giftCards}
        onCardUsed={loadGiftCards}
      />

      {/* Modal de création */}
      {showCreateModal && (
        <GiftCardCreateForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {/* Modal de détails */}
      <GiftCardDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        card={selectedCard}
        onCardUsed={loadGiftCards}
      />
    </div>
  );
};

export default GiftCardsPage;
